import { create } from 'zustand';
import type { TenantConfig, User, ThemeMode } from '../types';
import { storage, STORAGE_KEYS, getTenantThemeKey } from '@pp/shared/platform/storage';

interface AppState {
  // Tenant
  tenantConfig: TenantConfig | null;
  tenantLoading: boolean;
  tenantError: string | null;
  setTenantConfig: (config: TenantConfig | null) => void;
  updateTenantConfig: (updates: Partial<TenantConfig>) => void;
  setTenantLoading: (loading: boolean) => void;
  setTenantError: (error: string | null) => void;

  // Auth
  user: User | null;
  token: string | null;
  // SECURITY: refreshToken is now stored in HttpOnly cookie, not in JS-accessible storage
  // This property is kept for backward compatibility but always returns null
  refreshToken: string | null;
  isAuthenticated: boolean;
  // Native-only session flag: true once the user has passed the biometric
  // launch gate (Face ID / Touch ID) in the current process. Resets to false
  // when the app is killed or after logout. Not persisted.
  biometricUnlocked: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  // SECURITY: No-op - refresh token is managed via HttpOnly cookie by the server
  setRefreshToken: (refreshToken: string | null) => void;
  setBiometricUnlocked: (unlocked: boolean) => void;
  logout: () => void;

  // Theme
  theme: ThemeMode;
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: ThemeMode) => void;
  initializeTheme: (tenantId?: string) => void;
}

/**
 * Global application store using Zustand
 */
export const useAppStore = create<AppState>((set, get) => ({
  // Tenant state
  tenantConfig: null,
  tenantLoading: true,
  tenantError: null,

  setTenantConfig: (config) => set({ tenantConfig: config, tenantLoading: false }),
  updateTenantConfig: (updates) => {
    const { tenantConfig } = get();
    if (tenantConfig) {
      set({ tenantConfig: { ...tenantConfig, ...updates } });
    }
  },
  setTenantLoading: (loading) => set({ tenantLoading: loading }),
  setTenantError: (error) => set({ tenantError: error, tenantLoading: false }),

  // Auth state
  user: null,
  token: storage.get<string | null>(STORAGE_KEYS.JWT_TOKEN, null),
  // SECURITY: Refresh token is now in HttpOnly cookie - not accessible from JavaScript
  refreshToken: null,
  isAuthenticated: !!storage.get<string | null>(STORAGE_KEYS.JWT_TOKEN, null),
  biometricUnlocked: false,

  setUser: (user) => {
    set({ user, isAuthenticated: !!user });
    // Attribute Sentry events to the signed-in user (GUID only — no
    // email/name). Cleared on logout below. Dynamic import keeps Sentry
    // out of the auth-store's critical path on cold start.
    void import('../lib/sentry').then(({ setSentryUser }) => {
      if (user?.id) setSentryUser(user.id, user.tenantId);
    });
  },

  setBiometricUnlocked: (unlocked) => set({ biometricUnlocked: unlocked }),

  setToken: (token) => {
    if (token) {
      storage.set(STORAGE_KEYS.JWT_TOKEN, token);
    } else {
      storage.remove(STORAGE_KEYS.JWT_TOKEN);
    }
    set({ token, isAuthenticated: !!token });
  },

  // SECURITY: No-op - refresh token is managed via HttpOnly cookie by the server
  // Kept for backward compatibility with any code that might call this
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setRefreshToken: (_refreshToken) => {
    // Intentionally empty - refresh token is now in HttpOnly cookie
  },

  logout: () => {
    storage.remove(STORAGE_KEYS.JWT_TOKEN);
    // SECURITY: Refresh token cookie is cleared by the server via /auth/logout endpoint
    // Note: We intentionally keep the tenant subdomain on logout
    // Most users log back into the same tenant. Users switching tenants
    // can navigate to the new tenant's URL (with ?tenant= param) which
    // will automatically update the stored subdomain.
    // Tear down the biometric gate too — leaving it armed with a stale
    // session means the next launch would Face-ID them straight back in
    // even though they explicitly signed out.
    // .catch swallows are intentional: logout fires three best-effort
    // cleanups (biometric, push, sentry) and a teardown failure on any
    // of them must not turn into an unhandled rejection — at runtime it
    // would surface as a console error during logout, and in tests it
    // races worker teardown and fails CI.
    void import('@pp/shared/auth/biometricAuth')
      .then(({ disableBiometric }) => disableBiometric())
      .catch(() => {});
    // Unregister the push installation so further notifications don't
    // land on the now-logged-out device. Dynamic import keeps the
    // Capacitor plugin out of the web bundle's critical path.
    void import('@pp/shared/push/push')
      .then(({ disablePush }) => disablePush())
      .catch(() => {});
    // Drop user attribution from Sentry — subsequent crashes shouldn't
    // get tagged with the previous user's GUID.
    void import('../lib/sentry')
      .then(({ clearSentryUser }) => clearSentryUser())
      .catch(() => {});
    set({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      biometricUnlocked: false,
    });
  },

  // Theme state
  theme: 'system',
  effectiveTheme: 'light',

  setTheme: (theme) => {
    const { tenantConfig } = get();
    const themeKey = tenantConfig
      ? getTenantThemeKey(tenantConfig.tenantId)
      : STORAGE_KEYS.THEME_PREFERENCE;

    storage.set(themeKey, theme);

    // Calculate effective theme
    let effectiveTheme: 'light' | 'dark' = 'light';
    if (theme === 'system') {
      effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    } else {
      effectiveTheme = theme;
    }

    // Apply theme to DOM - use class for Tailwind v4
    if (effectiveTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    set({ theme, effectiveTheme });
  },

  initializeTheme: (tenantId) => {
    const themeKey = tenantId
      ? getTenantThemeKey(tenantId)
      : STORAGE_KEYS.THEME_PREFERENCE;

    const savedTheme = storage.get<ThemeMode>(themeKey, 'system');
    get().setTheme(savedTheme);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const { theme } = get();
      if (theme === 'system') {
        get().setTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
  },
}));
